import {
    IQueryContext,
    IQueryContextDatasourceColumn,
    IQueryContextDatasourceColumnState,
} from '../../services/query-context/models/IQueryContext';
import { GcpProjectId } from '../gcp-project-id.model';

/** A union string type representing ids of columns, which are filterable and splitable over datasources/tables.
 * It is not an enum because in the future we want to save some DatasourceColumns in db for further retrieval.
 * */
export type DatasourceColumnId =
    | 'ACTIVITY_APP_VERSION'
    | 'ACTIVITY_APP_VERSION_GROUP_MINOR'
    | 'ACTIVITY_DATE'
    | 'ACTIVITY_DATETIME'
    | 'ACTIVITY_DAY_NO'
    | 'ACTIVITY_HOUR'
    | 'ACTIVITY_WEEKDAY'
    | 'AD_NAME'
    | 'AD_CONSENT'
    | 'DAILY_AD_CONSENT'
    | 'ADMOB_APP_STORE'
    | 'FIRST_ATTRIBUTION_AD'
    | 'FIRST_ATTRIBUTION_AD_LENGTH_S'
    | 'FIRST_ATTRIBUTION_AD_SIZE'
    | 'FIRST_ATTRIBUTION_AD_ASPECT_RATIO'
    | 'FIRST_ATTRIBUTION_AD_TYPE'
    | 'FIRST_ATTRIBUTION_AD_THEME'
    | 'FIRST_ATTRIBUTION_AD_VERSION'
    | 'FIRST_ATTRIBUTION_ADSET'
    | 'FIRST_ATTRIBUTION_CAMPAIGN_ACTIVE'
    | 'FIRST_ATTRIBUTION_UNIFIED_CAMPAIGN'
    | 'FIRST_ATTRIBUTION_USER_MATCH_TYPE'
    /** Used as `traffic_source`: Organic, Non-Organic, CrossPromo */
    | 'FIRST_ATTRIBUTION_UNIFIED_TRAFFIC'
    // FIRST_ATTRIBUTION_UNIFIED_TRAFFIC = 'attribution.traffic_source',
    /** Google Ads, Facebok, etc. */
    | 'FIRST_ATTRIBUTION_UNIFIED_NETWORK'
    | 'BUSINESS_PROJECT'
    | 'BUSINESS_SERVICE'
    | 'BUSINESS_SERVICE_DETAILS_BIJOBS_JOB_ID_PROJECT'
    | 'BUSINESS_SERVICE_DETAILS_BIJOBS_JOB_ID_CATEGORY'
    | 'BUSINESS_SERVICE_DETAILS_BIJOBS_JOB_ID_NAME'
    | 'BUSINESS_SERVICE_DETAILS_BIJOBS_ORCHESTRATOR_OPTIONS_TYPE'
    | 'BUSINESS_SERVICE_DETAILS_BIJOBS_ORCHESTRATOR_OPTIONS_PERIOD'
    | 'CHAPTER_VERSION'
    | 'CHAPTER_NO'
    | 'CLOUD_PROJECT'
    | 'CLOUD_SERVICE'
    | 'CLOUD_SERVICE_DETAILS_BIGQUERY_STORAGE_DATASET_REF_PROJECT_ID'
    | 'CLOUD_SERVICE_DETAILS_BIGQUERY_STORAGE_DATASET_REF_DATASET_ID'
    | 'CLOUD_SKU'
    | 'CLOUD_SKU_SUMMARY'
    | 'COMPANY_PROJECT_NAME'
    | 'CUSTOM_FUNNELS_FUNNEL_NAME'
    | 'CUSTOM_FUNNELS_FIRST_GAME_ENI2'
    | 'CUSTOM_FUNNELS_OUT_REPLAYS'
    | 'CUSTOM_FUNNELS_STEP_ID'
    | 'CUSTOM_FUNNELS_STEP_NAME'
    | 'DAILY_TOTAL_ENERGY_SPENT'
    | 'DEVICES_SELECTED_FIRST_DEVICE_MODEL'
    | 'REVENUE_PAYER'
    | 'REVENUE_BADGE'
    | 'EVENT_PROJECT_ID'
    | 'FIREBASE_CHURNED'
    | 'FIRST_GEO_COUNTRY'
    | 'FIRST_GEO_COUNTRY_EU'
    | 'FIRST_GEO_COUNTRY_TIER'
    | 'FIRST_GEO_COUNTRY_IS_RELEVANT_LABEL'
    | 'FIRST_SESSION_DATE'
    | 'FIRST_APP_VERSION'
    | 'FIRST_APP_VERSION_ALIAS'
    | 'FIRST_APP_VERSION_GROUP'
    | 'FIRST_APP_VERSION_GROUP_MINOR'
    | 'FIREBASE_RANDOM'
    | 'FUNNELS_DETAILS_EVENT_NAME'
    | 'FUNNELS_DETAILS_SCREEN_NAME'
    | 'FUNNELS_MASTER_FUNNEL_STEP_ID'
    | 'FIRST_DEVICE_BRAND'
    | 'FIRST_DEVICE_CATEGORY'
    | 'FIRST_DEVICE_MODEL'
    | 'PROJECT_ID'
    | 'REALTIME_BATTERY_SAVE'
    | 'REALTIME_IS_ANONYMOUS'
    | 'REALTIME_IS_SMALL'
    | 'REALTIME_IS_BACKGROUND'
    | 'REALTIME_ERROR'
    | 'REALTIME_INTERNAL_ERROR'
    | 'REALTIME_IS_SUCCESS'
    | 'REALTIME_FAILURE_RATE_DEFINITION'
    | 'REVENUE_TYPE'
    | 'RESOURCES_SOURCE'
    | 'RESOURCE_NAME'
    | 'RESOURCE_PAYMENT_TYPE'
    | 'RESOURCE_SOURCE_NAME'
    | 'RESOURCE_SOURCE_CATEGORY'
    | 'CHANGE_TYPE'
    | 'SALES_INAPP_SOURCE'
    | 'SALES_INAPP_PLACEMENT'
    | 'SALES_INAPP_PRODUCT_ID'
    | 'SALES_INAPP_PRODUCT_ID_PRICE_TIER'
    | 'SALES_INAPP_PRODUCT_NAME'
    | 'SALES_OFFER_NAME'
    | 'SALES_OFFER_TYPE'
    | 'SALES_OFFER_SEGMENT'
    | 'SALES_OFFER_SEASON'
    | 'SALES_OFFER_SEASON_VARIANT'
    | 'SALES_OFFER_SEASON_SIZE'
    | 'SKAD_CONVERSION_VALUE'
    | 'VALIDATION_FLAG'
    | 'SEGMENT'
    | 'SEGMENT_EXCLUDE';

const DatasourceColumnName: Record<DatasourceColumnId, string> = {
    ACTIVITY_DATE: 'DATE',
    ACTIVITY_DATETIME: 'DATETIME',
    ACTIVITY_APP_VERSION: 'APP VERSION',
    ACTIVITY_APP_VERSION_GROUP_MINOR: 'APP VERSION.MINOR',
    ACTIVITY_DAY_NO: 'DAY NO',
    ACTIVITY_HOUR: 'HOUR',
    ACTIVITY_WEEKDAY: 'WEEKDAY',
    AD_NAME: 'AD NAME',
    AD_CONSENT: 'AD CONSENT STATUS',
    DAILY_AD_CONSENT: 'AD CONSENT STATUS',
    ADMOB_APP_STORE: 'ADMOB APP STORE',
    FIRST_ATTRIBUTION_AD: 'CAMPAIGN AD',
    FIRST_ATTRIBUTION_AD_LENGTH_S: 'CAMPAIGN AD LENGTH (SECONDS)',
    FIRST_ATTRIBUTION_AD_SIZE: 'CAMPAIGN AD SIZE',
    FIRST_ATTRIBUTION_AD_ASPECT_RATIO: 'CAMPAIGN AD ASPECT RATIO',
    FIRST_ATTRIBUTION_AD_TYPE: 'CAMPAIGN AD TYPE',
    FIRST_ATTRIBUTION_AD_THEME: 'CAMPAIGN AD THEME',
    FIRST_ATTRIBUTION_AD_VERSION: 'CAMPAIGN AD VERSION',
    FIRST_ATTRIBUTION_ADSET: 'CAMPAIGN ADSET',
    FIRST_ATTRIBUTION_CAMPAIGN_ACTIVE: 'CAMPAIGN ACTIVE',
    FIRST_ATTRIBUTION_UNIFIED_CAMPAIGN: 'CAMPAIGN NAME',
    FIRST_ATTRIBUTION_USER_MATCH_TYPE: 'USER MATCH TYPE',
    FIRST_ATTRIBUTION_UNIFIED_TRAFFIC: 'TRAFFIC SOURCE',
    FIRST_ATTRIBUTION_UNIFIED_NETWORK: 'NETWORK NAME',
    BUSINESS_PROJECT: 'BUSINESS PROJECT',
    BUSINESS_SERVICE: 'BUSINESS SERVICE',
    BUSINESS_SERVICE_DETAILS_BIJOBS_JOB_ID_PROJECT: 'BIJOBS JOB PROJECT',
    BUSINESS_SERVICE_DETAILS_BIJOBS_JOB_ID_CATEGORY: 'BIJOBS JOB CATEGORY',
    BUSINESS_SERVICE_DETAILS_BIJOBS_JOB_ID_NAME: 'BIJOBS JOB NAME',
    BUSINESS_SERVICE_DETAILS_BIJOBS_ORCHESTRATOR_OPTIONS_TYPE:
        'BIJOBS ORCHESTRATOR TYPE',
    BUSINESS_SERVICE_DETAILS_BIJOBS_ORCHESTRATOR_OPTIONS_PERIOD:
        'BIJOBS ORCHESTRATOR PERIOD',
    CHAPTER_NO: 'CHAPTER NO',
    CHAPTER_VERSION: 'CHAPTER VERSION',
    CLOUD_PROJECT: 'CLOUD PROJECT',
    CLOUD_SERVICE: 'CLOUD SERVICE',
    CLOUD_SERVICE_DETAILS_BIGQUERY_STORAGE_DATASET_REF_PROJECT_ID:
        'BIGQUERY DATASET PROJECT ID',
    CLOUD_SERVICE_DETAILS_BIGQUERY_STORAGE_DATASET_REF_DATASET_ID:
        'BIGQUERY DATASET DATASET ID',
    CLOUD_SKU: 'CLOUD SKU',
    CLOUD_SKU_SUMMARY: 'CLOUD SKU SUMMARY',
    COMPANY_PROJECT_NAME: 'PROJECT NAME',
    CUSTOM_FUNNELS_FUNNEL_NAME: 'FUNNEL',
    CUSTOM_FUNNELS_FIRST_GAME_ENI2: 'FIRST GAME ENI2',
    CUSTOM_FUNNELS_OUT_REPLAYS: 'OUT REPLAYS',
    CUSTOM_FUNNELS_STEP_ID: 'STEP ID',
    CUSTOM_FUNNELS_STEP_NAME: 'STEP NAME',
    DAILY_TOTAL_ENERGY_SPENT: 'DAILY TOTAL ENERGY SPENT',
    DEVICES_SELECTED_FIRST_DEVICE_MODEL: 'SELECTED DEVICE MODEL',
    REVENUE_PAYER: 'PAYER',
    REVENUE_BADGE: 'REVENUE BADGE',
    EVENT_PROJECT_ID: 'PROJECT ID',
    FIREBASE_CHURNED: 'CHURNED',
    FIRST_GEO_COUNTRY: 'COUNTRY',
    FIRST_GEO_COUNTRY_EU: 'COUNTRY EUROPEAN UNION',
    FIRST_GEO_COUNTRY_TIER: 'COUNTRY TIER',
    FIRST_GEO_COUNTRY_IS_RELEVANT_LABEL: 'COUNTRY IS RELEVANT',
    FIRST_SESSION_DATE: 'FIRST OPEN DATE',
    FIRST_APP_VERSION: 'INSTALL VERSION',
    FIRST_APP_VERSION_ALIAS: 'INSTALL VERSION ALIAS',
    FIRST_APP_VERSION_GROUP: 'INSTALL VERSION GROUP',
    FIRST_APP_VERSION_GROUP_MINOR: 'INSTALL VERSION.MINOR',
    FIREBASE_RANDOM: 'RANDOM',
    FUNNELS_DETAILS_EVENT_NAME: 'DETAILS EVENT NAME',
    FUNNELS_DETAILS_SCREEN_NAME: 'DETAILS SCREEN NAME',
    FUNNELS_MASTER_FUNNEL_STEP_ID: 'MASTER FUNNEL STEP ID',
    FIRST_DEVICE_BRAND: 'DEVICE BRAND',
    FIRST_DEVICE_CATEGORY: 'DEVICE CATEGORY',
    FIRST_DEVICE_MODEL: 'DEVICE MODEL',
    PROJECT_ID: 'PROJECT ID',
    REALTIME_BATTERY_SAVE: 'BATTERY SAVE MODE',
    REALTIME_IS_ANONYMOUS: 'IS ANONYMOUS',
    REALTIME_IS_SMALL: 'COMMIT IS SMALL',
    REALTIME_IS_BACKGROUND: 'IS BACKGROUND',
    REALTIME_ERROR: 'ERROR',
    REALTIME_INTERNAL_ERROR: 'INTERNAL ERROR',
    REALTIME_IS_SUCCESS: 'IS SUCCESS',
    REALTIME_FAILURE_RATE_DEFINITION: 'FAILURE RATE DEFINITION',
    REVENUE_TYPE: 'REVENUE TYPE',
    RESOURCES_SOURCE: 'RESOURCE SOURCE NAME',
    RESOURCE_NAME: 'RESOURCE NAME',
    RESOURCE_PAYMENT_TYPE: 'RESOURCE PAYMENT TYPE',
    RESOURCE_SOURCE_NAME: 'RESOURCE SOURCE NAME CELAN',
    RESOURCE_SOURCE_CATEGORY: 'RESOURCE INFLOW PAID SOURCE CATEGORY',
    CHANGE_TYPE: 'CHANGE TYPE',
    SALES_INAPP_PLACEMENT: 'PLACEMENT',
    SALES_INAPP_SOURCE: 'OFFER INAPP SOURCE',
    SALES_INAPP_PRODUCT_ID: 'INAPP PRODUCT ID',
    SALES_INAPP_PRODUCT_ID_PRICE_TIER: 'INAPP PRODUCT ID PRICE TIER',
    SALES_INAPP_PRODUCT_NAME: 'INAPP PRODUCT NAME',
    SALES_OFFER_NAME: 'OFFER',
    SALES_OFFER_TYPE: 'OFFER TYPE',
    SALES_OFFER_SEGMENT: 'OFFER SEGMENT',
    SALES_OFFER_SEASON: 'OFFER SEASON',
    SALES_OFFER_SEASON_VARIANT: 'OFFER SEASON VARIANT',
    SALES_OFFER_SEASON_SIZE: 'OFFER SEASON SIZE',
    SKAD_CONVERSION_VALUE: 'SKAD_CONVERSION_VALUE',
    VALIDATION_FLAG: 'VALIDATION FLAG',
    SEGMENT: 'SEGMENT',
    SEGMENT_EXCLUDE: 'EXCLUDE SEGMENT',
};

/**
 * An interface representing SQL tokens, which should be used in a SQL query.
 */
export interface ICompiledSQLTokens {
    /** initial value should be empty array */
    split_group: string[];
    /** initial value should be empty array */
    where: string[];
    /** unnestTableColumn should be placed right after `FROM <table>` statement. Initial value should be empty array */
    unnestDatasourceColumn: string[];
    /** initial value should be empty array */
    join: string[];
}

/**
 * An interface representing a SQL mapping of a column specific for a datasource.
 * @interface IDatasourceColumnSQLMap
 * @property {DatasourceColumnId|string} columnId - Id of the column.
 * @property {string} columnId - A column's SQL name in a specific datasource.
 * @property {function} toFilterSQL - A function that returns a SQL query string for filtering specific for a datasource.
 * @property {function} toSplitSQL - A function that returns a SQL query string for splitting specific for a datasource.
 */
export interface IDatasourceColumnSQLMap {
    columnId: DatasourceColumnId;
    columnSQL: string;
    compileSQLTokens?: (
        state: IQueryContextDatasourceColumnState,
        options?: { gcpProjectId: GcpProjectId }
    ) => Partial<ICompiledSQLTokens>;
    /* TODO remove this */
    toFilterSQL?: (state: IQueryContextDatasourceColumnState) => string;
    /* TODO remove this */
    toSplitSQL?: (state: IQueryContextDatasourceColumnState) => string;
}

/**
 * A type representing a datasource's dimensions.
 * @typedef DatasourceColumns
 * @type {Object.<DatasourceColumnId, IDatasourceColumnSQLMap>}
 */
export type IDatasourceColumnSQLMaps = Partial<
    Record<DatasourceColumnId, IDatasourceColumnSQLMap>
>;

/**
 * An interface representing a BQ table and its SQL mapping.
 * @interface IDatasourceSQLMap
 * @property {string} gcpProjectId - Optional. Id of a project.
 * @property {string} dataset - The name of the dataset.
 * @property {string} name - The name of the datasource.
 * @property {IDatasourceColumnSQLMaps} columns - A mapping of column ids into its SQL handlers.
 */
export interface IDatasourceSQLMap {
    gcpProjectId?: string;
    dataset: string;
    tableId: string;
    columns: IDatasourceColumnSQLMaps;
}

/**
 * Represents an object, which is used to compile SQL statements for a datasource.
 */
export interface ISQLStatementsCompiler {
    compileSQLStatements: (
        supportedDatasourceColumns: IQueryContextDatasourceColumn[],
        queryContextSettings: IQueryContext['settings'],
        options: { gcpProjectId: GcpProjectId },
        customCompiledSqlTokens?: Partial<ICompiledSQLTokens>
    ) => any;
}

/**
 * Constructs the full BigQuery table path given the GCP project ID, dataset, and table name.
 *
 * @param {string} gcpProjectId - The project ID of the BigQuery table.
 * @param {string} dataset - The dataset that the BigQuery table is located in.
 * @param {string} name - The name of the BigQuery table.
 * @returns {string} The fully constructed BigQuery table path.
 */
export function generateTableFullPath(
    gcpProjectId: string,
    dataset: string,
    name: string
) {
    return `${gcpProjectId}.${dataset}.${name}`;
}

function isDatasourceColumnIdArray(array: any[]): array is {
    id: DatasourceColumnId;
    compiledSqlTokens: Partial<ICompiledSQLTokens>;
}[] {
    return array.length > 0 && 'id' in array[0];
}
/**
 * Merges an array of SQL tokens into a single SQL token object.
 *
 * @param sqlTokensArray - An array of SQL token objects to merge.
 *
 * @returns A single SQL token object that is the result of merging all the SQL token objects in the input array.
 *
 * @example
 *
 * const sqlTokensArray = [
 *   { split_group: ['A'], where: ['B'], unnestDatasourceColumn: ['C'], join: ['D'] },
 *   { split_group: ['E'], where: ['F'], unnestDatasourceColumn: ['G'], join: ['H'] }
 * ];
 *
 * const result = mergeSqlTokensArray(sqlTokensArray);
 *
 * // Expected result:
 * // {
 * //   split_group: ['A', 'E'],
 * //   where: ['B', 'F'],
 * //   unnestDatasourceColumn: ['C', 'G'],
 * //   join: ['D', 'H']
 * // }
 */
export function mergeSqlTokensArray(
    sqlTokensArray:
        | Partial<ICompiledSQLTokens>[]
        | {
              id: DatasourceColumnId;
              compiledSqlTokens: Partial<ICompiledSQLTokens>;
          }[]
): ICompiledSQLTokens {
    // if id is provided, extract compiledSqlTokens, and sort by default comparator
    if (isDatasourceColumnIdArray(sqlTokensArray)) {
        sqlTokensArray = sqlTokensArray
            .sort((a, b) => sortComparatorDatasourceColumnIds(a.id, b.id))
            .map((item) => item.compiledSqlTokens);
    }
    return sqlTokensArray.reduce(
        (acc: ICompiledSQLTokens, cur) => {
            // console.log({
            //     msg: '[datasource.mergeSqlTokensArray].reduce',
            //     cur: JSONPrettyStringify(cur),
            // });

            if (cur.split_group)
                cur.split_group.forEach((split) => {
                    if (acc.split_group.findIndex((s) => s === split) == -1) {
                        acc.split_group.push(split);
                    }
                });
            if (cur.where)
                cur.where.forEach((where) => {
                    if (acc.where.findIndex((w) => w === where) == -1) {
                        acc.where.push(where);
                    }
                });
            if (cur.join)
                cur.join.forEach((join) => {
                    if (acc.join.findIndex((j) => j === join) == -1) {
                        acc.join.push(join);
                    }
                });
            if (cur.unnestDatasourceColumn)
                cur.unnestDatasourceColumn.forEach((unnestDatasourceColumn) => {
                    if (
                        acc.unnestDatasourceColumn.findIndex(
                            (u) => u === unnestDatasourceColumn
                        ) == -1
                    ) {
                        acc.unnestDatasourceColumn.push(unnestDatasourceColumn);
                    }
                });
            return acc;
        },
        {
            split_group: [],
            where: [],
            unnestDatasourceColumn: [],
            join: [],
        }
    );
}

/**
 * Generates a SQL CASE statement for ordering based on the provided datasource column ID.
 *
 * @param {DatasourceColumnId} datasourceColumnId - An identifier for a datasource column.
 * @param {Object} options - Configuration options for generating the query.
 * @param {string} [options.columnAlias='value'] - An alias for the column in the SQL statement.
 *
 * @returns {string} A SQL CASE statement used for ordering.
 *
 * @throws {Error} Throws an error if the provided datasourceColumnId is not recognized.
 *
 * @example
 *
 * const query = datasourceColumnOrderByQuery('REVENUE_BADGE', { columnAlias: 'revenue' });
 * // Generates a CASE statement based on the REVENUE_BADGE datasource column.
 */
export function datasourceColumnOrderByQuery(
    datasourceColumnId: DatasourceColumnId,
    options?: {
        columnAlias?: string;
    }
): string {
    const columnAlias = options?.columnAlias ?? 'value';
    switch (datasourceColumnId) {
        case 'ACTIVITY_WEEKDAY':
            return `
            CASE ${columnAlias}
                WHEN 'Monday' THEN 1
                WHEN 'Tuesday' THEN 2
                WHEN 'Wednesday' THEN 3
                WHEN 'Thursday' THEN 4
                WHEN 'Friday' THEN 5
                WHEN 'Saturday' THEN 6
                WHEN 'Sunday' THEN 7
                ELSE ERROR('Invalid weekday')
                END ASC`;
        case 'FIRST_ATTRIBUTION_AD_LENGTH_S':
            return `SAFE_CAST(${columnAlias} AS INT64) ASC`;
        case 'REVENUE_BADGE':
            return `
            CASE ${columnAlias}
              WHEN 'Water 0$' THEN 0
              WHEN 'Plankton < 5$' THEN 5
              WHEN 'Jellyfish < 10$' THEN 10
              WHEN 'Fish < 50$' THEN 50
              WHEN 'Seahorse < 25$' THEN 25
              WHEN 'Dolphin < 100$' THEN 100
              WHEN 'Baby Whale < 500$' THEN 500
              WHEN 'Whale < 1500$' THEN 1500
              WHEN 'Moby Dick >= 1500$' THEN cast('inf' as float64)
              ELSE NULL
            END ASC`;
        case 'ACTIVITY_APP_VERSION':
        case 'ACTIVITY_APP_VERSION_GROUP_MINOR':
        case 'FIRST_APP_VERSION':
        case 'FIRST_APP_VERSION_GROUP_MINOR':
            return `         
                SAFE_CAST(SPLIT(${columnAlias}, '.')[SAFE_OFFSET(0)] AS INT64) DESC,
                SAFE_CAST(SPLIT(${columnAlias}, '.')[SAFE_OFFSET(1)] AS INT64) DESC,
                SAFE_CAST(SPLIT(${columnAlias}, '.')[SAFE_OFFSET(2)] AS INT64) DESC,
                SAFE_CAST(SPLIT(${columnAlias}, '.')[SAFE_OFFSET(3)] AS INT64) DESC`;
        case 'SALES_OFFER_SEASON':
        case 'SALES_OFFER_SEASON_VARIANT':
        case 'SALES_OFFER_SEASON_SIZE':
            return `CASE
                      WHEN STARTS_WITH(${columnAlias}, '_') THEN 2
                      WHEN ${columnAlias} = 'NonSeasonalOffer' THEN 1
                      ELSE 0
                    END, ${columnAlias}`;
        case 'SALES_INAPP_PRODUCT_ID_PRICE_TIER':
            //
            return `COALESCE(SAFE_CAST(${columnAlias} AS INT64), 9999), ${columnAlias}`;
        case 'RESOURCE_PAYMENT_TYPE':
            return `
            CASE ${columnAlias}
                WHEN 'Free' THEN 1
                WHEN 'Paid' THEN 2
                WHEN 'Other' THEN 3
                WHEN '_NULL_' THEN 4
                ELSE 5
            END ASC`;
        default:
            throw Error(
                `datasourceColumnId '${datasourceColumnId}' not implemented`
            );
    }
}

/**
 * Retrieves the column name for a given column ID from the DatasourceColumnName enum.
 *
 * @param columnId - The ID of the column for which to retrieve the name.
 *
 * @returns The column name as a string.
 */
export function getDatasourceColumnName(columnId: DatasourceColumnId) {
    return DatasourceColumnName[columnId];
}

/**
 * Comparator function for sorting DatasourceColumnIds
 */
export function sortComparatorDatasourceColumnIds(
    a: DatasourceColumnId,
    b: DatasourceColumnId
): number {
    const priorityOrder = [
        'COMPANY_PROJECT_NAME',
        'FIRST_SESSION_DATE',
        'FIRST_GEO_COUNTRY_TIER',
        'FIRST_GEO_COUNTRY',
        'FIRST_ATTRIBUTION_UNIFIED_TRAFFIC',
    ];

    const lastOrder = [
        'SALES_INAPP_SOURCE',
        'SALES_INAPP_PLACEMENT',
        'SALES_INAPP_PRODUCT_ID',
        'SALES_INAPP_PRODUCT_ID_PRICE_TIER',
        'SALES_INAPP_PRODUCT_NAME',
        'SALES_OFFER_NAME',
        'SALES_OFFER_TYPE',
        'SALES_OFFER_SEGMENT',
        'SALES_OFFER_SEASON',
        'SALES_OFFER_SEASON_VARIANT',
        'SALES_OFFER_SEASON_SIZE',
    ];

    const aIndex = priorityOrder.indexOf(a);
    const bIndex = priorityOrder.indexOf(b);

    const aLastIndex = lastOrder.indexOf(a);
    const bLastIndex = lastOrder.indexOf(b);

    if (aIndex === -1 && bIndex === -1) {
        if (aLastIndex === -1 && bLastIndex === -1) {
            return a.localeCompare(b);
        } else if (aLastIndex === -1) {
            return -1;
        } else if (bLastIndex === -1) {
            return 1;
        } else {
            return aLastIndex - bLastIndex;
        }
    } else if (aIndex === -1) {
        return 1;
    } else if (bIndex === -1) {
        return -1;
    } else {
        return aIndex - bIndex;
    }
}
